import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import DashboardLayout from '../../components/DashboardLayout';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Slider } from '../../components/ui/slider';
import { Badge } from '../../components/ui/badge';
import { Checkbox } from '../../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { SignaturePad } from '../../components/ui/signature-pad';
import { toast } from 'sonner';
import { Loader2, ArrowLeft, Shield, Eye, FileText, Download, RotateCcw, ChevronDown, ChevronRight } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Terms content fetched from API
function TermsContent({ productId, token }) {
  const [articles, setArticles] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/placements/products/${productId}/terms`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {}
    }).then(res => {
      setArticles(res.data.articles || []);
    }).catch(() => {
      setArticles([]);
    }).finally(() => setLoading(false));
  }, [productId, token]);

  if (loading) return <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin" /></div>;
  if (!articles || articles.length === 0) return <p className="text-muted-foreground text-center py-8">Aucune condition disponible.</p>;

  return (
    <div className="py-4 space-y-5">
      {(articles || []).map((art, i) => (
        <div key={i}>
          {art.title && (
            <h3 className="text-white font-semibold text-sm uppercase tracking-wide mb-2 pb-2 border-b border-border/30">{art.title}</h3>
          )}
          <div className="text-sm text-gray-300 leading-relaxed whitespace-pre-line">{art.content}</div>
        </div>
      ))}
    </div>
  );
}

// Interactive Interest Schedule Simulator component
function InterestScheduleSimulator({ product, investmentAmount, interestPeriod, appliedRate }) {
  const [expanded, setExpanded] = useState(false);

  const schedule = useMemo(() => {
    if (!product || !investmentAmount || !appliedRate) return [];

    const ratePeriod = product.rentability_period || 'Mensuel';
    const periodMonthsMap = { 'Mensuel': 1, 'Trimestriel': 3, 'Semestriel': 6, 'Annuel': 12 };
    const baseMonths = periodMonthsMap[ratePeriod] || 1;
    const payoutPeriodMonths = periodMonthsMap[interestPeriod] || 1;
    const duration = product.duration_months || 12;
    const compound = product.interest_compound === true;
    const payoutTarget = product.interest_payout_target || 'available';

    const totalPeriods = Math.ceil(duration / payoutPeriodMonths);
    const periodRate = (appliedRate / 100) * (payoutPeriodMonths / baseMonths);

    let calcPrincipal = investmentAmount;
    let displayPrincipal = investmentAmount;
    let cumInterest = 0;
    const rows = [];

    if (interestPeriod === 'Fin de contrat') {
      const totalBasePeriods = duration / baseMonths;
      let interest;
      if (compound && payoutTarget === 'livret') {
        interest = investmentAmount * (Math.pow(1 + appliedRate / 100, totalBasePeriods) - 1);
      } else {
        interest = investmentAmount * (appliedRate / 100) * totalBasePeriods;
      }
      rows.push({
        month: duration,
        interest: interest,
        cumulative: interest,
        performance: (interest / investmentAmount) * 100,
        principal: payoutTarget === 'livret' ? investmentAmount + interest : investmentAmount,
      });
      return rows;
    }

    for (let i = 1; i <= totalPeriods; i++) {
      const interest = calcPrincipal * periodRate;
      cumInterest += interest;
      const perf = (cumInterest / investmentAmount) * 100;

      if (payoutTarget === 'livret') {
        displayPrincipal += interest;
        if (compound) calcPrincipal = displayPrincipal;
      }

      rows.push({
        month: i * payoutPeriodMonths,
        interest,
        cumulative: cumInterest,
        performance: perf,
        principal: displayPrincipal,
      });
    }
    return rows;
  }, [product, investmentAmount, interestPeriod, appliedRate]);

  if (!product || schedule.length === 0) return null;

  const totalInterest = schedule.length > 0 ? schedule[schedule.length - 1].cumulative : 0;
  const compound = product.interest_compound === true;

  return (
    <Card className="bg-card/80 backdrop-blur border-border/50" data-testid="interest-schedule-simulator">
      <button
        className="w-full p-6 flex items-center justify-between text-left"
        onClick={() => setExpanded(!expanded)}
        data-testid="schedule-toggle"
      >
        <div>
          <h3 className="text-lg font-bold text-white">Simulateur d&apos;échéancier</h3>
          <p className="text-sm text-muted-foreground mt-1">
            {compound ? 'Intérêts composés' : 'Intérêts simples'} — Total estimé : <span className="text-emerald-400 font-semibold">+{totalInterest.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €</span>
          </p>
        </div>
        {expanded ? <ChevronDown className="w-5 h-5 text-muted-foreground" /> : <ChevronRight className="w-5 h-5 text-muted-foreground" />}
      </button>
      {expanded && (
        <CardContent className="px-6 pb-6 pt-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="schedule-table">
              <thead>
                <tr className="border-b border-border text-muted-foreground">
                  <th className="text-left py-2 px-2">Mois</th>
                  <th className="text-right py-2 px-2">Intérêts versés</th>
                  <th className="text-right py-2 px-2">Intérêts cumulés</th>
                  <th className="text-right py-2 px-2">Performance</th>
                </tr>
              </thead>
              <tbody>
                {schedule.map((row, idx) => (
                  <tr key={idx} className="border-b border-border/30 hover:bg-secondary/20" data-testid={`schedule-row-${idx}`}>
                    <td className="py-2 px-2 font-mono text-muted-foreground">{row.month}</td>
                    <td className="py-2 px-2 text-right font-mono text-emerald-400">
                      +{row.interest.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €
                    </td>
                    <td className="py-2 px-2 text-right font-mono">
                      {row.cumulative.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €
                    </td>
                    <td className="py-2 px-2 text-right font-mono text-emerald-400">
                      +{row.performance.toFixed(2)}%
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="border-t-2 border-border font-semibold">
                  <td className="py-3 px-2">Total</td>
                  <td className="py-3 px-2 text-right font-mono text-emerald-400">—</td>
                  <td className="py-3 px-2 text-right font-mono text-emerald-400">
                    +{totalInterest.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €
                  </td>
                  <td className="py-3 px-2 text-right font-mono text-emerald-400">
                    +{schedule.length > 0 ? schedule[schedule.length - 1].performance.toFixed(2) : '0.00'}%
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </CardContent>
      )}
    </Card>
  );
}

export default function ClientPlacementDetail() {
  const { productId } = useParams();
  const { token, user, mirrorMode, mirrorClient } = useAuth();
  const navigate = useNavigate();
  const signaturePadRef = useRef(null);
  
  const [product, setProduct] = useState(null);
  const [category, setCategory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [generatingPdf, setGeneratingPdf] = useState(false);
  
  // Investment state
  const [investmentAmount, setInvestmentAmount] = useState(1000);
  const [interestPeriod, setInterestPeriod] = useState('Mensuel');
  
  // Subscription modal
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [showGainsModal, setShowGainsModal] = useState(false);
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [acceptAgreement, setAcceptAgreement] = useState(false);
  const [city, setCity] = useState('');

  const clientId = mirrorMode ? (mirrorClient?.user_id || mirrorClient?.id) : user?.id;
  const interestPeriodOptions = useMemo(() => {
    const labelMap = {
      monthly: 'Mensuel',
      quarterly: 'Trimestriel',
      biannual: 'Semestriel',
      annual: 'Annuel',
      end_of_contract: 'Fin de contrat'
    };

    if (product?.interest_periods) {
      const enabled = Object.entries(product.interest_periods)
        .filter(([, value]) => value)
        .map(([key]) => labelMap[key] || key);
      return enabled.length ? enabled : [product.rentability_period || 'Mensuel'];
    }
    return [
      'Mensuel',
      'Trimestriel',
      'Semestriel',
      'Annuel',
      'Fin de contrat'
    ];
  }, [product]);

  const fetchProduct = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/placements/products/${productId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProduct(res.data);
      setInvestmentAmount(res.data.min_investment || 1000);
      if (res.data.interest_periods) {
        const labelMap = {
          monthly: 'Mensuel',
          quarterly: 'Trimestriel',
          biannual: 'Semestriel',
          annual: 'Annuel',
          end_of_contract: 'Fin de contrat'
        };
        const enabledPeriods = Object.entries(res.data.interest_periods)
          .filter(([, value]) => value)
          .map(([key]) => labelMap[key] || key);
        const defaultPeriod = res.data.rentability_period && enabledPeriods.includes(res.data.rentability_period)
          ? res.data.rentability_period
          : (enabledPeriods[0] || 'Mensuel');
        setInterestPeriod(defaultPeriod);
      } else if (res.data.rentability_period) {
        setInterestPeriod(res.data.rentability_period);
      }
      
      // Fetch category
      if (res.data.category_id) {
        const catRes = await axios.get(`${API}/placements/categories/${res.data.category_id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setCategory(catRes.data);
      }
    } catch (error) {
      console.error('Failed to fetch product:', error);
      toast.error('Produit non trouvé');
      navigate('/client/placements');
    } finally {
      setLoading(false);
    }
  }, [productId, token, navigate]);

  useEffect(() => {
    fetchProduct();
  }, [fetchProduct]);

  // Calculate gains based on investment amount
  // Get rate based on investment amount using product's tiered rates or base rate
  const getRateForAmount = (amount) => {
    if (!product) return 0;
    const tiers = product.percentage_by_amount || [];
    if (tiers.length > 0) {
      const sorted = [...tiers].sort((a, b) => (parseFloat(a.min_amount) || 0) - (parseFloat(b.min_amount) || 0));
      for (const tier of sorted) {
        const min = parseFloat(tier.min_amount) || 0;
        const max = tier.max_amount != null && tier.max_amount !== '' && parseFloat(tier.max_amount) > 0 ? parseFloat(tier.max_amount) : Infinity;
        if (amount >= min && amount <= max) {
          return parseFloat(tier.percentage) || product.rentability_rate || 0;
        }
      }
      return parseFloat(sorted[sorted.length - 1]?.percentage) || product.rentability_rate || 0;
    }
    return product.rentability_rate || 0;
  };

  const calculateGains = () => {
    if (!product) return { gains: 0, total: 0, appliedRate: 0, monthlyGains: 0 };
    
    const rate = getRateForAmount(investmentAmount);
    const ratePeriod = product.rentability_period || 'Mensuel';
    const periodMonthsMap = { 'Mensuel': 1, 'Trimestriel': 3, 'Semestriel': 6, 'Annuel': 12 };
    const baseMonths = periodMonthsMap[ratePeriod] || 1;
    const compound = product.interest_compound === true;
    const payoutTarget = product.interest_payout_target || 'available';
    const duration = product.duration_months || 12;
    
    // Monthly rate for display
    const monthlyRate = (rate / 100) / baseMonths;
    const firstMonthGain = investmentAmount * monthlyRate;

    let totalGains;
    if (compound && payoutTarget === 'livret') {
      // Compound interest: simulate by payout period (same as schedule)
      const payoutMonths = periodMonthsMap[ratePeriod] || 1;
      const periodRate = (rate / 100);
      const totalPeriods = Math.floor(duration / payoutMonths);
      let principal = investmentAmount;
      totalGains = 0;
      for (let i = 0; i < totalPeriods; i++) {
        const interest = principal * periodRate;
        totalGains += interest;
        principal += interest;
      }
    } else {
      // Simple interest
      totalGains = firstMonthGain * duration;
    }
    
    return {
      gains: totalGains,
      total: investmentAmount + totalGains,
      appliedRate: rate,
      monthlyGains: firstMonthGain
    };
  };

  const handleSubscribe = async () => {
    if (!acceptTerms || !acceptAgreement) {
      toast.error('Veuillez accepter les conditions');
      return;
    }
    
    // Get signature data
    const signatureData = signaturePadRef.current?.getDataURL();
    if (!signatureData || signaturePadRef.current?.isEmpty()) {
      toast.error('Veuillez signer le contrat');
      return;
    }
    
    if (!city) {
      toast.error('Veuillez indiquer votre ville');
      return;
    }
    
    try {
      setSubmitting(true);
      
      await axios.post(`${API}/placements/subscriptions`, {
        product_id: productId,
        amount: investmentAmount,
        duration_months: product.duration_months,
        rentability_rate: product.rentability_rate,
        interest_period: interestPeriod,
        auto_renewal: product.auto_renewal,
        signature_data: signatureData,
        city: city
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Souscription réussie !');
      setShowSubscriptionModal(false);
      navigate('/client/contracts');
    } catch (error) {
      toast.error('Erreur', { description: error.response?.data?.detail || 'Impossible de souscrire' });
    } finally {
      setSubmitting(false);
    }
  };

  const handlePreviewContract = async () => {
    try {
      setGeneratingPdf(true);
      
      const response = await axios.post(`${API}/placements/contract/preview`, {
        product_id: productId,
        amount: investmentAmount,
        duration_months: product.duration_months,
        auto_renewal: product.auto_renewal,
        city: city,
        interest_period: interestPeriod,
        client_id: clientId
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Convert base64 to blob and open in new tab
      const pdfData = atob(response.data.pdf_base64);
      const bytes = new Uint8Array(pdfData.length);
      for (let i = 0; i < pdfData.length; i++) {
        bytes[i] = pdfData.charCodeAt(i);
      }
      const blob = new Blob([bytes], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');
    } catch (error) {
      toast.error('Erreur', { description: 'Impossible de générer l\'aperçu' });
    } finally {
      setGeneratingPdf(false);
    }
  };

  const handleDownloadContract = async () => {
    try {
      setGeneratingPdf(true);
      
      const signatureData = signaturePadRef.current?.getDataURL();
      
      const response = await axios.post(`${API}/placements/contract/generate`, {
        product_id: productId,
        amount: investmentAmount,
        duration_months: product.duration_months,
        auto_renewal: product.auto_renewal,
        city: city,
        signature_image: signatureData
      }, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      // Download the PDF
      const url = URL.createObjectURL(response.data);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Contrat-${product.name.replace(/\s+/g, '-')}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      toast.success('Contrat téléchargé !');
    } catch (error) {
      toast.error('Erreur', { description: 'Impossible de télécharger le contrat' });
    } finally {
      setGeneratingPdf(false);
    }
  };

  const handleClearForm = () => {
    signaturePadRef.current?.clear();
    setAcceptTerms(false);
    setAcceptAgreement(false);
    setCity('');
  };

  const { gains, total, appliedRate, monthlyGains } = calculateGains();

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-[400px]">
          <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
        </div>
      </DashboardLayout>
    );
  }

  if (!product) return null;

  return (
    <DashboardLayout>
      <div className="space-y-6 max-w-6xl mx-auto">
        {/* Back Button */}
        <Button 
          variant="ghost" 
          className="gap-2"
          onClick={() => navigate(-1)}
          data-testid="back-btn"
        >
          <ArrowLeft className="w-4 h-4" />
          Retour
        </Button>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Content - Left Side */}
          <div className="lg:col-span-2 space-y-6">
            {/* Hero Image */}
            <div className="relative rounded-2xl overflow-hidden h-72 bg-gradient-to-br from-blue-900 to-indigo-900">
              {product.images?.[0] ? (
                <img 
                  src={product.images[0]} 
                  alt={product.name}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <div className="w-32 h-32 rounded-2xl bg-white/10 backdrop-blur flex items-center justify-center">
                    <Shield className="w-16 h-16 text-white/50" />
                  </div>
                </div>
              )}
              
              {/* Overlay with logo and title */}
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-6">
                <div className="bg-blue-500 rounded-xl p-4 flex items-center gap-4">
                  <div className="w-12 h-12 rounded-lg bg-white/20 flex items-center justify-center">
                    <Shield className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h1 className="text-xl font-bold text-white">{product.name}</h1>
                    <p className="text-sm text-blue-200 uppercase">
                      {category?.title || 'OFFRES DE PLACEMENT'}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Investment Slider */}
            <Card className="bg-card/80 backdrop-blur border-border/50">
              <CardContent className="p-6 space-y-6">
                {/* Amount Range */}
                <div className="flex items-center justify-between text-sm">
                  <span className="text-blue-400 font-bold">
                    {product.min_investment?.toLocaleString('fr-FR')} €
                  </span>
                  <span className="text-blue-400 font-bold">
                    {product.max_investment?.toLocaleString('fr-FR')} €
                  </span>
                </div>

                {/* Slider */}
                <Slider
                  value={[investmentAmount]}
                  onValueChange={([val]) => setInvestmentAmount(val)}
                  min={product.min_investment || 1000}
                  max={product.max_investment || 500000}
                  step={100}
                  className="w-full"
                  data-testid="investment-slider"
                />

                {/* Scale markers */}
                <div className="flex justify-between text-xs text-muted-foreground">
                  {[0, 25, 50, 75, 100].map((percent) => {
                    const value = (product.min_investment || 1000) + 
                      (((product.max_investment || 500000) - (product.min_investment || 1000)) * percent / 100);
                    return (
                      <span key={percent}>{Math.round(value).toLocaleString('fr-FR')}</span>
                    );
                  })}
                </div>

                {/* Details Grid */}
                <div className="grid grid-cols-2 gap-4 pt-4 border-t border-border/50">
                  <div className="flex justify-between items-center">
                    <span className="text-muted-foreground">Durée</span>
                    <Badge variant="outline" className="bg-muted/30">
                      {product.duration_months} mois
                    </Badge>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-muted-foreground">Taux appliqué</span>
                    <Badge variant="outline" className="bg-emerald-500/20 text-emerald-400 border-emerald-500/30">
                      {appliedRate}% /{product.rentability_period === 'Mensuel' ? 'mois' : product.rentability_period === 'Trimestriel' ? 'trim.' : product.rentability_period === 'Annuel' ? 'an' : product.rentability_period?.toLowerCase() || 'an'}
                    </Badge>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-muted-foreground">Montant</span>
                    <Input 
                      type="text"
                      inputMode="numeric"
                      value={investmentAmount || ''}
                      onChange={(e) => {
                        const val = e.target.value.replace(/[^0-9]/g, '').replace(/^0+/, '');
                        setInvestmentAmount(val ? Number(val) : '');
                      }}
                      onFocus={(e) => e.target.select()}
                      className="w-32 text-right"
                      data-testid="amount-input"
                    />
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-muted-foreground">{product.interest_compound && product.interest_payout_target === 'livret' ? 'Intérêt 1er mois' : 'Intérêt mensuel'}</span>
                    <span className="text-emerald-400 font-medium">
                      +{monthlyGains.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-muted-foreground">Gains totaux</span>
                    <span className="text-emerald-400 font-bold">
                      +{gains.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-muted-foreground">Capital + Intérêts</span>
                    <span className="text-emerald-400 font-bold text-lg">
                      {total.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €
                    </span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Description */}
            {product.description && (
              <Card className="bg-card/80 backdrop-blur border-border/50">
                <CardContent className="p-6">
                  <h2 className="text-xl font-bold text-white mb-4">
                    Compte d&apos;Épargne {product.name}
                  </h2>
                  <div className="flex items-start gap-2 text-blue-400 mb-4">
                    <span>◆</span>
                    <span className="text-lg">Une Épargne Nouvelle Génération</span>
                  </div>
                  <div 
                    className="prose prose-invert prose-sm max-w-none text-gray-300 [&_h3]:text-white [&_h3]:text-base [&_h3]:font-semibold [&_h3]:mt-5 [&_h3]:mb-2 [&_p]:my-2.5 [&_p]:leading-relaxed [&_ul]:my-3 [&_ul]:space-y-1.5 [&_ol]:my-3 [&_ol]:space-y-1.5 [&_li]:leading-relaxed [&_strong]:text-white/90 [&_em]:text-blue-300/80"
                    dangerouslySetInnerHTML={{ __html: product.description }}
                  />
                </CardContent>
              </Card>
            )}

            {/* Rate Grid Card - Dynamic from product tiers */}
            {(product.percentage_by_amount?.length > 0) && (
            <Card className="bg-card/80 backdrop-blur border-border/50">
              <CardContent className="p-6">
                <h3 className="text-lg font-bold text-white mb-4">Grille des taux NET</h3>
                <div className="space-y-2 text-sm">
                  {[...product.percentage_by_amount]
                    .sort((a, b) => (parseFloat(a.min_amount) || 0) - (parseFloat(b.min_amount) || 0))
                    .map((tier, idx) => {
                      const min = parseFloat(tier.min_amount) || 0;
                      const max = tier.max_amount != null && tier.max_amount !== '' ? parseFloat(tier.max_amount) : null;
                      const pct = parseFloat(tier.percentage) || 0;
                      const isActive = investmentAmount >= min && (max === null || investmentAmount <= max);
                      return (
                        <div 
                          key={idx} 
                          className={`flex justify-between items-center py-2 px-3 rounded ${
                            isActive ? 'bg-emerald-500/20 border border-emerald-500/30' : 'bg-muted/20'
                          }`}
                        >
                          <span className="text-muted-foreground">
                            {min.toLocaleString('fr-FR')} € - {max !== null ? `${max.toLocaleString('fr-FR')} €` : '∞'}
                          </span>
                          <span className="text-emerald-400 font-bold">{pct}%</span>
                        </div>
                      );
                    })}
                </div>
              </CardContent>
            </Card>
            )}

            {/* Interactive Interest Schedule Simulator */}
            <InterestScheduleSimulator 
              product={product} 
              investmentAmount={investmentAmount}
              interestPeriod={interestPeriod}
              appliedRate={appliedRate}
            />
          </div>

          {/* Sidebar - Right Side */}
          <div className="space-y-6">
            {/* Product Details Card */}
            <Card className="bg-card/80 backdrop-blur border-border/50">
              <CardContent className="p-6 space-y-4">
                {/* Details List */}
                {[
                  { label: 'Durée', value: `${product.duration_months} mois` },
                  { label: 'Fonds', value: product.funds_locked ? 'Bloqués (durée du contrat)' : 'Disponible' },
                  { label: 'Rentabilité', value: (() => {
                    const pba = product.percentage_by_amount;
                    if (pba && pba.length > 1) {
                      const rates = pba.map(t => parseFloat(t.percentage)).sort((a,b) => a - b);
                      return `${rates[0]}% à ${rates[rates.length - 1]}% ${product.rentability_period}`;
                    }
                    return `${appliedRate}% ${product.rentability_variable > 0 ? `à ${product.rentability_variable}%` : ''} ${product.rentability_period}`;
                  })() },
                  { label: 'Investissement minimum', value: `${product.min_investment?.toLocaleString('fr-FR')} €` },
                  { label: 'Investissement maximum', value: `${product.max_investment?.toLocaleString('fr-FR')} €` },
                  { label: 'Reconduction', value: product.auto_renewal ? 'Oui' : 'Non' },
                  { label: 'Garantie contractuelle', value: product.contract_guarantee ? 'Oui' : 'Non' },
                  { label: 'Indice de confiance', value: `${product.confidence_index}/100` },
                ].map((item, index) => (
                  <div key={index} className="flex justify-between items-center py-2 border-b border-border/30 last:border-0">
                    <span className="text-muted-foreground text-sm">{item.label}</span>
                    <span className="text-white font-medium text-sm">{item.value}</span>
                  </div>
                ))}

                {/* Document Links */}
                <div className="flex justify-between items-center py-2 border-b border-border/30">
                  <span className="text-muted-foreground text-sm">Conditions générales</span>
                  <Button variant="outline" size="sm" className="text-xs h-7" onClick={() => setShowTermsModal(true)} data-testid="view-terms-btn">
                    <Eye className="w-3 h-3 mr-1" />
                    VOIR
                  </Button>
                </div>
                <div className="flex justify-between items-center py-2">
                  <span className="text-muted-foreground text-sm">Récapitulatif des gains</span>
                  <Button variant="outline" size="sm" className="text-xs h-7" onClick={() => setShowGainsModal(true)} data-testid="view-gains-btn">
                    <Eye className="w-3 h-3 mr-1" />
                    VOIR
                  </Button>
                </div>

                {/* CTA Button */}
                <Button 
                  className="w-full bg-blue-500 hover:bg-blue-600 text-white font-bold py-6 text-lg mt-4"
                  onClick={() => setShowSubscriptionModal(true)}
                  data-testid="subscribe-btn"
                >
                  Je me lance
                  <div className="text-xs font-normal mt-1 opacity-80">
                    à partir de {product.min_investment?.toLocaleString('fr-FR')} €
                  </div>
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Subscription Modal */}
        <Dialog open={showSubscriptionModal} onOpenChange={setShowSubscriptionModal}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-xl">Souscription</DialogTitle>
            </DialogHeader>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 py-4">
              {/* Left Column - Product Info Recap */}
              <div className="space-y-4">
                {[
                  { label: 'Durée', value: `${product.duration_months} mois` },
                  { label: 'Fonds', value: product.funds_locked ? 'Bloqués (durée du contrat)' : 'Disponible' },
                  { label: 'Rentabilité', value: (() => {
                    const pba = product.percentage_by_amount;
                    if (pba && pba.length > 1) {
                      const rates = pba.map(t => parseFloat(t.percentage)).sort((a,b) => a - b);
                      return `${rates[0]}% à ${rates[rates.length - 1]}% ${product.rentability_period}`;
                    }
                    return `${appliedRate}% ${product.rentability_period}`;
                  })() },
                  { label: 'Investissement minimum', value: `${product.min_investment?.toLocaleString('fr-FR')} €` },
                  { label: 'Investissement maximum', value: `${product.max_investment?.toLocaleString('fr-FR')} €` },
                  { label: 'Reconduction', value: product.auto_renewal ? 'Oui' : 'Non' },
                  { label: 'Garantie contractuelle', value: product.contract_guarantee ? 'Oui' : 'Non' },
                  { label: 'Indice de confiance', value: `${product.confidence_index}/100` },
                ].map((item, index) => (
                  <div key={index} className="flex justify-between items-center text-sm border-b border-border/30 pb-2">
                    <span className="text-muted-foreground">{item.label}</span>
                    <span className="font-medium">{item.value}</span>
                  </div>
                ))}
              </div>

              {/* Right Column - Form */}
              <div className="space-y-4">
                <h3 className="font-semibold text-lg">Formulaire de souscription</h3>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-xs text-muted-foreground">Prénom</Label>
                    <p className="font-medium">{user?.first_name}</p>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs text-muted-foreground">Nom</Label>
                    <p className="font-medium">{user?.last_name}</p>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-xs text-muted-foreground">IP de la transaction</Label>
                    <p className="font-medium text-sm">127.0.0.1</p>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs text-muted-foreground">Fait le (Date)</Label>
                    <p className="font-medium">{new Date().toLocaleDateString('fr-FR')}</p>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>À (Ville) *</Label>
                  <Input 
                    value={city}
                    onChange={(e) => setCity(e.target.value)}
                    placeholder="Votre ville"
                    data-testid="city-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Montant *</Label>
                  <Input 
                    type="text"
                    inputMode="numeric"
                    value={investmentAmount || ''}
                    onChange={(e) => {
                      const val = e.target.value.replace(/[^0-9]/g, '').replace(/^0+/, '');
                      setInvestmentAmount(val ? Number(val) : '');
                    }}
                    onFocus={(e) => e.target.select()}
                    data-testid="modal-amount-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Période d&apos;intérêt</Label>
                  <Select value={interestPeriod} onValueChange={setInterestPeriod}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                        <SelectContent>
                          {interestPeriodOptions.map((period) => (
                            <SelectItem key={period} value={period} data-testid={`interest-period-${period}`}>
                              {period}
                            </SelectItem>
                          ))}
                        </SelectContent>
                  </Select>
                </div>

                {/* Electronic Signature Pad */}
                <SignaturePad
                  ref={signaturePadRef}
                  width={300}
                  height={120}
                  label="Signature électronique *"
                  penColor="#1a365d"
                  backgroundColor="#ffffff"
                  data-testid="signature-pad"
                />
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-4 py-4 border-t border-border/50">
              <Button 
                variant="outline" 
                className="flex-1 gap-2"
                onClick={handlePreviewContract}
                disabled={generatingPdf}
              >
                {generatingPdf ? <Loader2 className="w-4 h-4 animate-spin" /> : <Eye className="w-4 h-4" />}
                Aperçu du contrat
              </Button>
              <Button 
                variant="outline" 
                className="flex-1 gap-2 text-orange-400 border-orange-400/50 hover:bg-orange-400/10"
                onClick={handleClearForm}
              >
                <RotateCcw className="w-4 h-4" />
                Effacer &amp; Recommencer
              </Button>
            </div>

            {/* Checkboxes */}
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <Checkbox 
                  id="agreement"
                  checked={acceptAgreement}
                  onCheckedChange={setAcceptAgreement}
                  data-testid="accept-agreement"
                />
                <Label htmlFor="agreement" className="text-sm leading-tight cursor-pointer">
                  Bon pour accord
                </Label>
              </div>
              <div className="flex items-start gap-3">
                <Checkbox 
                  id="terms"
                  checked={acceptTerms}
                  onCheckedChange={setAcceptTerms}
                  data-testid="accept-terms"
                />
                <Label htmlFor="terms" className="text-sm leading-tight cursor-pointer">
                  J&apos;accepte les Termes et Conditions.
                </Label>
              </div>
            </div>

            <DialogFooter className="flex-col gap-3 sm:flex-col">
              <Button 
                variant="outline"
                className="w-full gap-2"
                onClick={handleDownloadContract}
                disabled={generatingPdf}
              >
                {generatingPdf ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                Télécharger le contrat signé
              </Button>
              <Button 
                className="w-full bg-blue-500 hover:bg-blue-600"
                onClick={handleSubscribe}
                disabled={submitting || !acceptTerms || !acceptAgreement || !city}
                data-testid="validate-subscription-btn"
              >
                {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                Valider ma souscription
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Terms & Conditions Modal */}
        <Dialog open={showTermsModal} onOpenChange={setShowTermsModal}>
          <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-xl">Conditions Générales — {product.name}</DialogTitle>
            </DialogHeader>
            <TermsContent productId={product.id} token={token} />
          </DialogContent>
        </Dialog>

        {/* Gains Summary Modal */}
        <Dialog open={showGainsModal} onOpenChange={setShowGainsModal}>
          <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-xl">Récapitulatif des gains</DialogTitle>
            </DialogHeader>
            <div className="py-4 space-y-4">
              {/* Summary */}
              <div className="grid grid-cols-2 gap-3">
                <div className="p-3 rounded-lg bg-muted/30 text-center">
                  <p className="text-xs text-muted-foreground">Montant investi</p>
                  <p className="text-lg font-bold text-white">{investmentAmount.toLocaleString('fr-FR')} €</p>
                </div>
                <div className="p-3 rounded-lg bg-muted/30 text-center">
                  <p className="text-xs text-muted-foreground">Taux appliqué</p>
                  <p className="text-lg font-bold text-emerald-400">{appliedRate}% {product.rentability_period}</p>
                </div>
                <div className="p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/20 text-center">
                  <p className="text-xs text-muted-foreground">Gains totaux</p>
                  <p className="text-lg font-bold text-emerald-400">+{gains.toLocaleString('fr-FR', { minimumFractionDigits: 2 })} €</p>
                </div>
                <div className="p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/20 text-center">
                  <p className="text-xs text-muted-foreground">Capital + Intérêts</p>
                  <p className="text-lg font-bold text-emerald-400">{total.toLocaleString('fr-FR', { minimumFractionDigits: 2 })} €</p>
                </div>
              </div>

              {/* Schedule Table */}
              <div>
                <h4 className="font-semibold text-sm mb-3 text-muted-foreground">Échéancier estimé ({product.interest_compound && product.interest_payout_target === 'livret' ? 'Intérêts composés' : 'Intérêts simples'})</h4>
                <div className="rounded-lg border border-border/50 overflow-hidden">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-muted/30 text-xs text-muted-foreground">
                        <th className="py-2 px-3 text-left">Mois</th>
                        <th className="py-2 px-3 text-right">Intérêts</th>
                        <th className="py-2 px-3 text-right">Cumulés</th>
                        <th className="py-2 px-3 text-right">Perf.</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(() => {
                        const ratePeriod = product.rentability_period || 'Mensuel';
                        const periodMap = { 'Mensuel': 1, 'Trimestriel': 3, 'Semestriel': 6, 'Annuel': 12 };
                        const baseMonths = periodMap[ratePeriod] || 1;
                        const duration = product.duration_months || 12;
                        const compound = product.interest_compound === true;
                        const payoutTarget = product.interest_payout_target || 'available';
                        const monthlyRate = (appliedRate / 100) / baseMonths;
                        
                        let principal = investmentAmount;
                        let cumInterest = 0;
                        const rows = [];
                        
                        for (let i = 1; i <= duration; i++) {
                          const interest = principal * monthlyRate;
                          cumInterest += interest;
                          const perf = (cumInterest / investmentAmount) * 100;
                          if (compound && payoutTarget === 'livret') principal += interest;
                          rows.push({ month: i, interest, cumInterest, perf });
                        }
                        
                        return rows.map((r, idx) => (
                          <tr key={idx} className="border-t border-border/20 hover:bg-muted/10">
                            <td className="py-1.5 px-3 text-muted-foreground">{r.month}</td>
                            <td className="py-1.5 px-3 text-right text-emerald-400">+{r.interest.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €</td>
                            <td className="py-1.5 px-3 text-right font-medium">{r.cumInterest.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €</td>
                            <td className="py-1.5 px-3 text-right text-emerald-400">+{r.perf.toFixed(2)}%</td>
                          </tr>
                        ));
                      })()}
                    </tbody>
                    <tfoot>
                      <tr className="bg-muted/20 border-t border-border/50 font-semibold">
                        <td className="py-2 px-3">Total</td>
                        <td className="py-2 px-3 text-right">—</td>
                        <td className="py-2 px-3 text-right text-emerald-400">+{gains.toLocaleString('fr-FR', { minimumFractionDigits: 2 })} €</td>
                        <td className="py-2 px-3 text-right text-emerald-400">+{((gains / investmentAmount) * 100).toFixed(2)}%</td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
